import { useEffect, useRef, useState } from 'preact/hooks';
import { Tooltip } from './Tooltip';
import { route } from 'preact-router';
import { BASE_PATH } from '../../shared/types';
import { logout } from '../lib/auth';
import { AppIcon } from './AppIcon';
import { DeleteAccount } from './DeleteAccount';

export function AccountMenu({ email, isAdmin = false, compactLabel }: { email: string; isAdmin?: boolean; compactLabel?: string }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  useEffect(() => {
    if (open) panel.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  }, [open, settings]);

  const show = () => {
    setSettings(false);
    setError(null);
    setOpen(true);
  };
  const signOut = async () => {
    setBusy(true);
    setError(null);
    try {
      await logout();
      route(`${BASE_PATH}/`);
    } catch {
      setError('Could not sign out. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div class="account-menu" ref={root}>
      <Tooltip text="Account menu"><button class="account-trigger" type="button" ref={trigger}
        aria-label={`Account menu for ${email}`} aria-haspopup="dialog"
        aria-expanded={open} aria-controls="account-popover"
        onClick={() => open ? setOpen(false) : show()}
        onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); if (open && !settings) panel.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true }); else show(); } }}>
        <span class={compactLabel ? 'account-label-full' : undefined}>{email}</span>
        {compactLabel && <span class="account-label-compact">{compactLabel}</span>}<AppIcon name="chevron-down" />
      </button></Tooltip>
      {open && (
        <div class={`account-popover${settings ? ' account-popover-settings' : ''}`} id="account-popover"
          role="dialog" aria-label={settings ? 'Account settings' : 'Account menu'} ref={panel}>
          {settings ? (
            <>
              <button class="account-back" type="button" onClick={() => setSettings(false)}>
                <AppIcon name="arrow-left" /> Back to account menu
              </button>
              <DeleteAccount email={email} />
            </>
          ) : (
            <>
              <div class="account-popover-heading"><p>Signed in as</p><strong>{email}</strong></div>
              <div class="account-actions">
                {isAdmin && <button class="account-action" type="button" onClick={() => { setOpen(false); route(`${BASE_PATH}/admin`); }}>
                  <AppIcon name="forms" /> Administration
                </button>}
                <button class="account-action" type="button" onClick={() => { setOpen(false); route(`${BASE_PATH}/feedback`); }}>
                  <AppIcon name="mail" /> Feedback
                </button>
                <button class="account-action" type="button" onClick={() => setSettings(true)}>
                  <AppIcon name="settings" /> Account settings
                </button>
                <button class="account-action" type="button" disabled={busy} onClick={signOut}>
                  <AppIcon name="sign-out" /> {busy ? 'Signing out…' : 'Sign out'}
                </button>
              </div>
            </>
          )}
          {error && <p class="alert" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
